/**
 * Add to Log Page
 * Adds comments to existing iTop tickets
 */

import './taskpane.css';
import { CredentialService } from '../services/credential.service';
import { ITopApiService } from '../services/itop-api.service';
import { EmailService } from '../services/email.service';
import { ITopOrganization, ITopTicket } from '../models/itop.models';
import {
  getElementById,
  showMessage,
  hideMessage,
  setButtonLoading,
  populateSelect,
  waitForOffice,
  goToSettings,
  truncate
} from '../utils/helpers';

// Declare Quill as global
declare const Quill: {
  new (selector: string | HTMLElement, options?: Record<string, unknown>): QuillInstance;
};

interface QuillInstance {
  root: HTMLElement;
  getSemanticHTML(): string;
  setText(text: string): void;
  clipboard: {
    dangerouslyPasteHTML(html: string): void;
  };
}

// Editor instance
let commentEditor: QuillInstance | null = null;

// DOM Elements
let messageDiv: HTMLElement;
let loadingDiv: HTMLElement;
let formDiv: HTMLElement;
let ticketIdInput: HTMLInputElement;
let lookupBtn: HTMLButtonElement;
let organizationSelect: HTMLSelectElement;
let ticketLoadingDiv: HTMLElement;
let ticketTable: HTMLTableElement;
let ticketTableBody: HTMLElement;
let noTicketsDiv: HTMLElement;
let selectedTicketInfo: HTMLElement;
let selectedTicketDisplay: HTMLElement;
let addLogBtn: HTMLButtonElement;

// Data
let organizations: ITopOrganization[] = [];
let selectedTicket: ITopTicket | null = null;

/**
 * Initialize the page
 */
async function initialize(): Promise<void> {
  await waitForOffice();

  // Check credentials
  if (!CredentialService.hasCredentials()) {
    showMessage(
      getElementById('message')!,
      'Please configure your iTop credentials first.',
      'warning'
    );
    setTimeout(goToSettings, 2000);
    return;
  }

  // Get DOM elements
  messageDiv = getElementById<HTMLElement>('message')!;
  loadingDiv = getElementById<HTMLElement>('loading')!;
  formDiv = getElementById<HTMLElement>('form')!;
  ticketIdInput = getElementById<HTMLInputElement>('ticketId')!;
  lookupBtn = getElementById<HTMLButtonElement>('lookupBtn')!;
  organizationSelect = getElementById<HTMLSelectElement>('organization')!;
  ticketLoadingDiv = getElementById<HTMLElement>('ticketLoading')!;
  ticketTable = getElementById<HTMLTableElement>('ticketTable')!;
  ticketTableBody = getElementById<HTMLElement>('ticketTableBody')!;
  noTicketsDiv = getElementById<HTMLElement>('noTickets')!;
  selectedTicketInfo = getElementById<HTMLElement>('selectedTicketInfo')!;
  selectedTicketDisplay = getElementById<HTMLElement>('selectedTicketDisplay')!;
  addLogBtn = getElementById<HTMLButtonElement>('addLogBtn')!;

  // Set up event listeners
  getElementById('settingsBtn')?.addEventListener('click', goToSettings);
  lookupBtn.addEventListener('click', handleLookup);
  organizationSelect.addEventListener('change', handleOrganizationChange);
  addLogBtn.addEventListener('click', handleAddToLog);

  // Load data
  await loadInitialData();
}

/**
 * Load initial data
 */
async function loadInitialData(): Promise<void> {
  loadingDiv.classList.add('show');
  formDiv.style.display = 'none';
  hideMessage(messageDiv);

  try {
    // Load organizations
    organizations = await ITopApiService.getOrganizations();

    // Populate organization dropdown
    populateSelect(
      organizationSelect,
      [{ id: 0, name: 'All organizations' }, ...organizations],
      'id',
      'name'
    );

    // Load email body for comment
    await loadEmailBody();

    // Show form
    loadingDiv.classList.remove('show');
    formDiv.style.display = 'block';

    // Initialize TinyMCE
    await initializeEditor();

  } catch (error) {
    loadingDiv.classList.remove('show');
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    showMessage(messageDiv, `Failed to load data: ${errorMessage}`, 'error');
  }
}

/**
 * Load email body for comment
 */
async function loadEmailBody(): Promise<void> {
  if (!EmailService.isReady()) {
    return;
  }

  try {
    const emailData = await EmailService.getEmailData();
    (window as unknown as { emailBody: string }).emailBody = emailData.bodyType === 'html'
      ? EmailService.cleanHtmlBody(emailData.body)
      : EmailService.textToHtml(emailData.body);
  } catch (error) {
    console.warn('Failed to load email body:', error);
  }
}

/**
 * Initialize Quill editor
 */
function initializeEditor(): void {
  try {
    commentEditor = new Quill('#comment', {
      theme: 'snow',
      modules: {
        toolbar: [
          ['bold', 'italic', 'underline'],
          [{ 'list': 'ordered' }, { 'list': 'bullet' }],
          ['link'],
          ['clean']
        ]
      },
      placeholder: 'Enter comment...'
    });

    // Set email body if available
    const emailBody = (window as unknown as { emailBody: string }).emailBody;
    if (emailBody && commentEditor) {
      commentEditor.clipboard.dangerouslyPasteHTML(emailBody);
    }
  } catch (error) {
    console.error('Failed to initialize Quill:', error);
  }
}

/**
 * Handle ticket ID lookup
 */
async function handleLookup(): Promise<void> {
  const ticketId = parseInt(ticketIdInput.value);

  if (!ticketId || isNaN(ticketId)) {
    showMessage(messageDiv, 'Please enter a valid ticket ID.', 'error');
    return;
  }

  setButtonLoading(lookupBtn, true, 'Looking up...');
  hideMessage(messageDiv);

  try {
    const ticket = await ITopApiService.getTicketById(ticketId);

    if (ticket) {
      selectTicket(ticket);
      showMessage(messageDiv, `Found: ${ticket.ref} - ${ticket.title}`, 'success');
    } else {
      showMessage(messageDiv, `Ticket #${ticketId} not found.`, 'error');
    }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    showMessage(messageDiv, `Lookup failed: ${errorMessage}`, 'error');
  } finally {
    setButtonLoading(lookupBtn, false);
  }
}

/**
 * Handle organization filter change
 */
async function handleOrganizationChange(): Promise<void> {
  const orgId = parseInt(organizationSelect.value);

  // Clear selection
  selectedTicket = null;
  updateSelectedDisplay();

  // Show loading
  ticketLoadingDiv.classList.add('show');
  ticketTable.style.display = 'none';
  noTicketsDiv.style.display = 'none';

  try {
    let tickets: ITopTicket[] = [];

    if (orgId) {
      tickets = await ITopApiService.getTicketsForOrganization(orgId);
    }

    renderTicketTable(tickets);

  } catch (error) {
    console.error('Failed to load tickets:', error);
    renderTicketTable([]);
  } finally {
    ticketLoadingDiv.classList.remove('show');
  }
}

/**
 * Render ticket table
 */
function renderTicketTable(tickets: ITopTicket[]): void {
  ticketTableBody.innerHTML = '';

  if (tickets.length === 0) {
    ticketTable.style.display = 'none';
    noTicketsDiv.style.display = 'block';
    return;
  }

  noTicketsDiv.style.display = 'none';
  ticketTable.style.display = 'table';

  for (const ticket of tickets) {
    const row = document.createElement('tr');
    row.innerHTML = `
      <td>
        <input type="radio" name="ticketSelect" value="${ticket.id}"
               data-ref="${ticket.ref}" data-title="${ticket.title.replace(/"/g, '&quot;')}">
      </td>
      <td>${ticket.ref}</td>
      <td title="${ticket.title}">${truncate(ticket.title, 40)}</td>
      <td>${ticket.status}</td>
    `;

    // Add click handler for radio
    const radio = row.querySelector('input[type="radio"]') as HTMLInputElement;
    radio.addEventListener('change', () => {
      selectTicket(ticket);
    });

    // Add row click handler
    row.addEventListener('click', (e) => {
      if ((e.target as HTMLElement).tagName !== 'INPUT') {
        radio.checked = true;
        selectTicket(ticket);
      }
    });

    ticketTableBody.appendChild(row);
  }
}

/**
 * Select a ticket
 */
function selectTicket(ticket: ITopTicket): void {
  selectedTicket = ticket;
  updateSelectedDisplay();
}

/**
 * Update selected ticket display
 */
function updateSelectedDisplay(): void {
  if (selectedTicket) {
    selectedTicketInfo.style.display = 'block';
    selectedTicketDisplay.textContent = `${selectedTicket.ref} - ${selectedTicket.title}`;
    addLogBtn.disabled = false;
  } else {
    selectedTicketInfo.style.display = 'none';
    selectedTicketDisplay.textContent = '-';
    addLogBtn.disabled = true;
  }
}

/**
 * Handle add to log button
 */
async function handleAddToLog(): Promise<void> {
  if (!selectedTicket) {
    showMessage(messageDiv, 'Please select a ticket first.', 'error');
    return;
  }

  const comment = commentEditor ? commentEditor.getSemanticHTML() : '';

  if (!comment.trim()) {
    showMessage(messageDiv, 'Please enter a comment.', 'error');
    return;
  }

  setButtonLoading(addLogBtn, true, 'Adding...');
  hideMessage(messageDiv);

  try {
    await ITopApiService.addToPublicLog(selectedTicket.id, comment);

    showMessage(
      messageDiv,
      `Comment added to ${selectedTicket.ref} successfully!`,
      'success'
    );

    // Clear comment
    if (commentEditor) {
      commentEditor.setText('');
    }

    // Clear selection
    selectedTicket = null;
    updateSelectedDisplay();

    // Uncheck radio buttons
    const radios = document.querySelectorAll<HTMLInputElement>('input[name="ticketSelect"]');
    radios.forEach(r => r.checked = false);

  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    showMessage(messageDiv, `Failed to add comment: ${errorMessage}`, 'error');
  } finally {
    setButtonLoading(addLogBtn, false);
  }
}

// Register for email item changes
EmailService.onItemChanged(() => {
  loadEmailBody();
});

// Initialize when Office is ready
Office.onReady(() => {
  initialize();
});
